/**
 * sl-728o.9: Copy-to-Clipboard for text content on Shapes and Notes.
 *
 * Adds a toolbar button that copies the plaintext content of the selected
 * shape or note to the system clipboard.
 */

import { toast } from '@blocksuite/affine-components/toast';
import {
  NoteBlockModel,
  ShapeElementModel,
  TextElementModel,
} from '@blocksuite/affine-model';
import type {
  ToolbarAction,
  ToolbarContext,
} from '@blocksuite/affine-shared/services';
import { ActionPlacement } from '@blocksuite/affine-shared/services';
import { CopyIcon } from '@blocksuite/icons/lit';
import type { GfxModel } from '@blocksuite/std/gfx';

import { edgelessToolbarI18nT } from './i18n';

/**
 * Extract plaintext from a surface model.
 * - ShapeElementModel / TextElementModel: model.text (Y.Text)
 * - NoteBlockModel: recursive children text extraction
 */
function extractPlaintext(model: GfxModel): string {
  // Shape or Text element — has .text directly
  if (model instanceof ShapeElementModel || model instanceof TextElementModel) {
    return model.text?.toString() ?? '';
  }

  // Note — extract text from child blocks recursively
  if (model instanceof NoteBlockModel) {
    return extractBlockChildrenText(model);
  }

  return '';
}

/**
 * Recursively extract text from a block's children.
 * Each block with a .text property contributes a line.
 */
function extractBlockChildrenText(block: {
  children: Array<{ text?: { toString(): string }; children: Array<unknown> }>;
}): string {
  const lines: string[] = [];
  for (const child of block.children) {
    if (child.text) {
      lines.push(child.text.toString());
    }
    if (child.children?.length) {
      const nested = extractBlockChildrenText(child as typeof block);
      if (nested) lines.push(nested);
    }
  }
  return lines.join('\n');
}

/**
 * Check if a model has extractable text content.
 */
function hasTextContent(model: GfxModel): boolean {
  if (model instanceof ShapeElementModel || model instanceof TextElementModel) {
    const text = model.text?.toString();
    return !!text && text.length > 0;
  }
  if (model instanceof NoteBlockModel) {
    return extractBlockChildrenText(model).length > 0;
  }
  return false;
}

export const copyTextAction = {
  placement: ActionPlacement.End,
  id: 'a.copy-text',
  get label() {
    return edgelessToolbarI18nT('toolbar.copyText');
  },
  get tooltip() {
    return edgelessToolbarI18nT('toolbar.copyText');
  },
  icon: CopyIcon(),
  when(ctx: ToolbarContext) {
    const models = ctx.getSurfaceModels();
    if (models.length !== 1) return false;
    return hasTextContent(models[0]);
  },
  run(ctx: ToolbarContext) {
    const models = ctx.getSurfaceModels();
    if (models.length !== 1) return;

    const text = extractPlaintext(models[0]);
    if (!text) return;

    navigator.clipboard.writeText(text).then(
      () => toast(ctx.host, edgelessToolbarI18nT('toast.copiedToClipboard')),
      () => toast(ctx.host, edgelessToolbarI18nT('toast.copyFailed'))
    );
  },
} satisfies ToolbarAction;
